// Global Stats Service - uses metadata endpoint for min/max/avg values
// OPTIMIZED: No longer loads ALL cells individually!
import { getFilterMetadata } from './filterDataService.js'
import { startPhase, updatePhaseProgress, completePhase } from '../utils/loadingService.js'

let cachedStats = null

/**
 * Get global statistics for radar chart normalization
 * Uses the /metadata endpoint instead of loading all cells
 * Returns min/max/avg values for energy, power, capacity, current in all three bases
 */
export async function getGlobalStats() {
  if (cachedStats) return cachedStats

  startPhase('GLOBAL_STATS');

  try {
    console.log('[globalStats] 🚀 Loading global stats from metadata endpoint (FAST!)...')
    const startTime = Date.now()
    
    updatePhaseProgress('GLOBAL_STATS', 0.3);
    
    // Get metadata from the backend /metadata endpoint (cached in filterDataService)
    const metadata = await getFilterMetadata()
    
    updatePhaseProgress('GLOBAL_STATS', 0.7);
    
    const duration = Date.now() - startTime
    console.log(`[globalStats] ✓ Global stats loaded from metadata in ${duration}ms`)
    
    // Transform metadata ranges to globalStats format
    // Metadata format: { energy: { abs: {min, max, avg, ...}, grav: {...}, vol: {...} }, ... }
    // GlobalStats format: { energy: { abs: {min, max, minCell, maxCell}, grav: {...}, vol: {...} }, ... }
    
    const transformRange = (rangeData, defaultMin = 0, defaultMax = 100, siToDisplay = 1) => {
      if (!rangeData) {
        return {
          min: defaultMin,
          max: defaultMax,
          avg: (defaultMin + defaultMax) / 2,
          minCell: 'unknown',
          maxCell: 'unknown'
        }
      }
      return {
        // Note: metadata is already in display units (Wh, W, Ah, A), 
        // but RadarChart expects SI units (J, W, C, A)
        // We need to convert BACK to SI for the radar chart normalization
        min: (rangeData.min || defaultMin) * siToDisplay,
        max: (rangeData.max || defaultMax) * siToDisplay,
        avg: (rangeData.avg || (rangeData.min + rangeData.max) / 2) * siToDisplay,
        minCell: 'unknown', // Metadata doesn't include which cell has min/max
        maxCell: 'unknown'
      }
    }
    
    // Energy: metadata is in Wh, RadarChart uses J (multiply by 3600)
    const energyStats = {
      abs: transformRange(metadata.energy?.abs, 3, 15, 3600), // Wh → J
      grav: transformRange(metadata.energy?.grav, 100, 300, 3600), // Wh/kg → J/kg
      vol: transformRange(metadata.energy?.vol, 200, 800, 3600e3) // Wh/L → J/m³
    }
    
    // Power: metadata is in W, RadarChart uses W (no conversion needed for abs/grav)
    const powerStats = {
      abs: transformRange(metadata.power?.abs, 10, 200, 1), // W
      grav: transformRange(metadata.power?.grav, 500, 3000, 1), // W/kg
      vol: transformRange(metadata.power?.vol, 1, 10, 1000) // W/L → W/m³
    }
    
    // Capacity: metadata is in Ah, RadarChart uses C (multiply by 3600)
    const capacityStats = {
      abs: transformRange(metadata.capacity?.abs, 2, 5, 3600), // Ah → C
      grav: transformRange(metadata.capacity?.grav, 30, 85, 3600), // Ah/kg → C/kg
      vol: transformRange(metadata.capacity?.vol, 70, 200, 3600e3) // Ah/L → C/m³
    }
    
    // Current: metadata is in A, RadarChart uses A (no conversion needed for abs/grav)
    const currentStats = {
      abs: transformRange(metadata.current?.abs, 5, 50, 1), // A
      grav: transformRange(metadata.current?.grav, 100, 1000, 1), // A/kg
      vol: transformRange(metadata.current?.vol, 0.3, 3, 1000) // A/L → A/m³
    }
    
    // Batch variation and aging from metadata (already in %)
    // RadarChart expects fractions (0.05 = 5%)
    const batchVarStats = metadata.batchVariation || { min: 0.5, max: 5, avg: 2 }
    // Aging can be negative (capacity gain) or positive (capacity loss)
    // min could be negative (best case: capacity gain), max is positive (worst case: capacity loss)
    const agingStats = metadata.aging || { min: -0.5, max: 5, avg: 1 }
    
    updatePhaseProgress('GLOBAL_STATS', 0.98);

    // Build cachedStats in the format RadarChart expects
    cachedStats = {
      // Energy - All basis types (J in SI)
      energy: energyStats,
      
      // Power - All basis types (W in SI)
      power: powerStats,
      
      // Capacity - All basis types (C in SI)
      capacity: capacityStats,
      
      // Current - All basis types (A in SI)
      current: currentStats,
      
      // Batch variation (lower is better) - as fraction
      minBatchVar: (batchVarStats.min || 0.5) / 100,
      maxBatchVar: (batchVarStats.max || 5) / 100,
      avgBatchVar: (batchVarStats.avg || 2) / 100,
      minBatchVarCell: 'unknown',
      maxBatchVarCell: 'unknown',
      
      // Initial aging (lower is better, can be negative for capacity gain) - as fraction
      // Note: min can be negative (capacity gain = best), max is positive (capacity loss = worst)
      minInitialAging: (agingStats.min ?? -0.5) / 100,
      maxInitialAging: (agingStats.max ?? 5) / 100,
      avgInitialAging: (agingStats.avg ?? 1) / 100,
      minInitialAgingCell: 'unknown',
      maxInitialAgingCell: 'unknown',
      
      // Legacy fields for backward compatibility (use grav basis)
      minEnergy: energyStats.grav.min,
      maxEnergy: energyStats.grav.max,
      avgEnergy: energyStats.grav.avg,
      minEnergyCell: 'unknown',
      maxEnergyCell: 'unknown',
      
      minPower: powerStats.grav.min,
      maxPower: powerStats.grav.max,
      avgPower: powerStats.grav.avg,
      minPowerCell: 'unknown',
      maxPowerCell: 'unknown',
      
      // Basis-aware averages
      avgEnergyBasis: {
        abs: energyStats.abs.avg,
        grav: energyStats.grav.avg,
        vol: energyStats.vol.avg
      },
      avgPowerBasis: {
        abs: powerStats.abs.avg,
        grav: powerStats.grav.avg,
        vol: powerStats.vol.avg
      },
      avgCapacityBasis: {
        abs: capacityStats.abs.avg,
        grav: capacityStats.grav.avg,
        vol: capacityStats.vol.avg
      },
      avgCurrentBasis: {
        abs: currentStats.abs.avg,
        grav: currentStats.grav.avg,
        vol: currentStats.vol.avg
      },
      
      // Legacy averages (for backward compatibility - use grav)
      avgCapacity: capacityStats.grav.avg,
      avgCurrent: currentStats.grav.avg
    }

    console.log('🔍 Global Stats from Metadata (SI units):')
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    console.log('📊 Energy (W_C10):')
    console.log(`  Gravimetric: ${(cachedStats.energy.grav.min/3600).toFixed(1)} - ${(cachedStats.energy.grav.max/3600).toFixed(1)} Wh/kg`)
    console.log('⚡ Power (P_peak):')
    console.log(`  Gravimetric: ${cachedStats.power.grav.min.toFixed(0)} - ${cachedStats.power.grav.max.toFixed(0)} W/kg`)
    console.log('🎯 Batch Variation:', `${(cachedStats.minBatchVar*100).toFixed(2)}% - ${(cachedStats.maxBatchVar*100).toFixed(2)}%`)
    console.log('🔋 Initial Aging:', `${(cachedStats.minInitialAging*100).toFixed(2)}% - ${(cachedStats.maxInitialAging*100).toFixed(2)}%`)
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    
    completePhase('GLOBAL_STATS');
    return cachedStats
  } catch (err) {
    console.error('[globalStats] Failed to load global stats:', err)
    completePhase('GLOBAL_STATS');
    
    // Return fallback values (SI units)
    return {
      energy: {
        abs: { min: 10000, max: 50000, avg: 30000 },
        grav: { min: 500000, max: 1080000, avg: 750000 },
        vol: { min: 1000000000, max: 2500000000, avg: 1500000000 }
      },
      power: {
        abs: { min: 10, max: 200, avg: 100 },
        grav: { min: 500, max: 3000, avg: 1500 },
        vol: { min: 1000000, max: 10000000, avg: 5000000 }
      },
      capacity: {
        abs: { min: 7200, max: 14400, avg: 10000 },
        grav: { min: 140000, max: 300000, avg: 200000 },
        vol: { min: 300000000, max: 700000000, avg: 500000000 }
      },
      current: {
        abs: { min: 5, max: 50, avg: 25 },
        grav: { min: 100, max: 1000, avg: 500 },
        vol: { min: 300000, max: 3000000, avg: 1500000 }
      },
      minBatchVar: 0.005,
      maxBatchVar: 0.05,
      avgBatchVar: 0.02,
      minInitialAging: 0.005,
      maxInitialAging: 0.05,
      avgInitialAging: 0.02,
      // Legacy
      minEnergy: 500000,
      maxEnergy: 1080000,
      avgEnergy: 750000,
      minPower: 500,
      maxPower: 3000,
      avgPower: 1500,
      avgCapacity: 200000,
      avgCurrent: 500
    }
  }
}

// Clear cache (call when new cells are added)
export function clearStatsCache() {
  cachedStats = null
}
